import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardContent,
  Grid,
  Box,
  Toolbar,
  IconButton,
  Tooltip,
  TextField,
  Typography,
  Button,
  Dialog,
  FormControl,
  FormControlLabel,
  Switch,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import { SalesLinesProps, SalesLineViewModel, SalesLineDocumentResultModel, Option, VariantOption, ListQuery } from "../../Types/ExportTypes";
import { useAuth, useSnackbar, useLoading } from "../../Context/Contexts";
import { AccountAutoComplete, CertificateAccountAutoComplete, ItemAutoComplete } from "../../components/AutoComplete/AutoCompletes";
import AsyncAutocomplete from "../../components/AsyncAutoComplete";
import VariantAutoComplete from "../AutoComplete/Item/VariantAutoComplete";

import LocalizedNumberInputs from "../../components/Input/LocalizedNumberInputs"
import { formatNumber } from '../../Utils/NumberUtils';
import { useTranslation } from "react-i18next";
import ImageBox from "../../components/Image/ImageBox";

import { generateVariantLabel } from '../../Utils/VariantUtils';
import ResponsivePagination from "../ResponsivePagination";
import ScannerDialog from "../Scanner/ScannerDialog";

const SalesDocumentLines: React.FC<SalesLinesProps> = ({ salesHeaderViewModel, readUrl, updateUrl, deleteUrl, history = false, onLinesChanged, preventIfUnsaved, showHasChangesError, locked }) => {
  const { authService } = useAuth();
  const { showSnackbar } = useSnackbar();

  const [query, setQuery] = useState<ListQuery>({ page: 0, pageSize: 2, filter: "" });
  const { showLoading, hideLoading } = useLoading();
  const [isLinesLoading, setIsLineLoading] = useState(false);
  const [salesLines, setSalesLines] = useState<SalesLineViewModel[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [editingLine, setEditingLine] = useState<SalesLineViewModel | null>(null);
  const [items, setItems] = useState<Option[]>([]);
  const [accounts, setAccounts] = useState<Option[]>([]);
  const [variants, setVariants] = useState<VariantOption[]>([]);
  const { t } = useTranslation();
  const [showAddAdvanced, setShowAddAdvanced] = useState(false);
  const [rowMode, setRowMode] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);

  const hasPendingChanges = () => (editingLine?.propertiesChanged?.length ?? 0) > 0;
  const preventIfUnsavedChanges = () => {
    if (hasPendingChanges()) {
      showHasChangesError?.();
      return true;
    }
    return preventIfUnsaved?.() ?? false;
  };

  useEffect(() => {
    if (isLoading) showLoading();
    else hideLoading();
  }, [isLoading, showLoading, hideLoading]);




  const fetchCertificateAccounts = async (
  ): Promise<Option[]> => {
    const url = `/autocompleteApi/GetCertificateAccounts`;

    const response = await authService.get<Option[]>(url);
    return response || [];
  };





  const fetchSalesLines = useCallback(async () => {
    setIsLoading(true);
    setIsLineLoading(true);
    try {
      const response = await authService.post<SalesLineDocumentResultModel>(
        `${readUrl}?documentNo=${salesHeaderViewModel.no}&documentType=${salesHeaderViewModel.documentType}&history=${history}`,
        query
      );
      setSalesLines(response.items);
      setTotalCount(response.count);
    } catch (error: any) {
      console.error("Failed to fetch lines:", error);
      showSnackbar(`${t("dialog.fetchDataError")} : ${error.response?.data ?? error.message}`, "error");
    } finally {
      setIsLoading(false);
      setIsLineLoading(false);
    }
  }, [authService, salesHeaderViewModel.documentType, salesHeaderViewModel.no, query, readUrl, history]);

  useEffect(() => {
    fetchSalesLines();
    // fetchItemOptions("", 0).then(setItems);
  }, [fetchSalesLines, query]);


  const toggleRowMode = () => {
    const newRowMode = !rowMode;
    setRowMode(newRowMode);
    setQuery({ ...query, page: 0, pageSize: newRowMode ? 20 : 2 });
  };

  const handleAddNewLine = async (type?: string) => {
    if (preventIfUnsavedChanges() || !validateLineEditing()) return;
    setIsLoading(true);
    setVariants([]);

    var chosenAccountNo = null;
    if (type === "certificate") {
      const accounts = await fetchCertificateAccounts();
      chosenAccountNo = accounts[0];
    }
    setEditingLine({
      id: 0,
      no: "",
      barcode: null,

      quantity: 1,
      unitPrice: null,
      quantityToShip: 1,
      outstandingQuantity: null,
      lineDiscountAmountIncVAT: null,
      amountIncludingVat: null,
      img_Url: null,
      imageExist: null,
      lineNo: null,
      documentNo: salesHeaderViewModel.no,
      isNew: true,
      isAccountCreate: type === "account",
      isItemCreate: type === "item",
      isCertificateCreate: type === "certificate",
      isTextCreate: type === "text",
      accountNo: chosenAccountNo?.value,
      description: chosenAccountNo?.description,
    });


    setIsLoading(false);
  };

  const handleEditRow = async (line: SalesLineViewModel) => {
    if (preventIfUnsavedChanges() || !validateLineEditing()) return;
    setIsLoading(true);

    if (line.itemNo) {

    }
    else if (line.accountNo) {

    }
    setEditingLine(line);
    setIsLoading(false);
  };




  //const handleFieldChange = async (field: keyof SalesLine, value: any) => {
  //    if (editingLine) {
  //        const updatedLine = { ...editingLine, [field]: value };

  //        if (field === "itemNo") {
  //            console.log("field change");
  //            updatedLine.accountNo = null;
  //            const fetchedVariants = await fetchVariants({ itemNo: updatedLine.itemNo ?? "" });
  //            setVariants(fetchedVariants);
  //        }

  //        if (field === "accountNo") {
  //            updatedLine.itemNo = null;
  //            updatedLine.variantId = null;
  //        }

  //        setEditingLine(updatedLine);
  //    }
  //};


  const handleFieldChanges = async (changes: Partial<SalesLineViewModel>) => {
    if (!editingLine) return;

    const updatedPropertiesChanged = [...(editingLine.propertiesChanged ?? [])];

    for (const key of Object.keys(changes) as (keyof SalesLineViewModel)[]) {
      const existing = updatedPropertiesChanged.find(p => p.key === key);
      if (existing) {
        existing.propertyFetched = false;
      } else {
        updatedPropertiesChanged.push({ key, propertyFetched: false });
      }
    }

    const updatedLine = {
      ...editingLine,
      ...changes,
      propertiesChanged: updatedPropertiesChanged,
    };

    setEditingLine(updatedLine);
  };


  const handleFieldChange = async (field: keyof SalesLineViewModel, value: any) => {
    if (editingLine) {
      let updatedLine = { ...editingLine, [field]: value }

      if (field === "itemNo") {

        updatedLine.accountNo = null;

      }

      if (field === "accountNo") {
        updatedLine.itemNo = null;
        updatedLine.variantId = null;
      }

      setEditingLine((prev) => {
        if (!prev) return null;

        const updatedPropertiesChanged = prev.propertiesChanged ? [...prev.propertiesChanged] : [];


        const existingProperty = updatedPropertiesChanged.find((p) => p.key === field);

        if (existingProperty) {
          existingProperty.propertyFetched = false; // set so we on change refetch connected properties
        } else {
          updatedPropertiesChanged.push({ key: field, propertyFetched: false });
        }

        updatedLine = { ...prev, [field]: value, propertiesChanged: updatedPropertiesChanged };
        return updatedLine;
      });

    };
  }

  const handleSaveLine = async () => {

    if (!editingLine) return;

    if (!validateLineEditing(editingLine)) return;

    try {
      setIsLoading(true);
      setIsLineLoading(true);
      const updatedLine = await authService.post<SalesLineViewModel>(updateUrl, editingLine);

      if (editingLine.id !== 0) {
        // Update existing row in the list
        setSalesLines((prev) =>
          prev.map((line) => (line.id === updatedLine.id ? updatedLine : line))
        );
      } else {
        // Determine the correct page to navigate to
        const newTotalCount = totalCount + 1;
        const lastPage = Math.ceil(newTotalCount / query.pageSize) - 1;

        setQuery((prev) => ({
          ...prev,
          page: salesLines.length < query.pageSize ? prev.page : lastPage,
          totalCount: newTotalCount,
        }));
      }
      showSnackbar(t("dialog.lineSaved"), "success");
      setEditingLine(null);
      onLinesChanged?.();
    } catch (error: any) {
      console.error("Error saving line:", error);
      showSnackbar(`${t("dialog.lineSaveError")} : ${error.response?.data ?? error.message}`, "error");

    } finally {
      setIsLoading(false);
      setIsLineLoading(false);
    }
  };

  const handleRowDelete = async (salesLine: SalesLineViewModel) => {
    const isConfirmed = window.confirm(t("dialog.deleteRowConfirm"));
    if (!isConfirmed) return;
    try {
      await authService.post(deleteUrl, salesLine);
      setSalesLines((prev) => prev.filter((line) => line.id !== salesLine.id));
      showSnackbar(t("dialog.rowDeleted"), "success");
    } catch (error: any) {
      console.error("Failed to delete row:", error);
      showSnackbar(`${t("dialog.rowDeleteError")} : ${error.response?.data ?? error.message}`, "error");

    }
    setQuery((prev) => ({ ...prev, page: 0, totalCount: totalCount - 1 }))
    onLinesChanged?.();
  };

  const deleteAll = async () => {
    const isConfirmed = window.confirm(t("dialog.confirmDeleteAllLines"));
    if (!isConfirmed) return;
    try {
      setIsLineLoading(true);
      await authService.post(`/salesDocumentApi/DeleteSalesDocumentLines?documentNo=${salesHeaderViewModel.no}&documentType=${salesHeaderViewModel.documentType}`, {});
      setSalesLines([]);
      showSnackbar(t("dialog.allRowsDeleted"), "success");

    } catch (error: any) {
      console.error("Failed to delete rows:", error);
      showSnackbar(`${t("dialog.rowsDeleteError")} : ${error.response?.data ?? error.message}`, "error");

    }
    finally {
      setIsLineLoading(false);
    }
    setQuery((prev) => ({ ...prev, page: 0, totalCount: totalCount - 1 }))
    onLinesChanged?.();
  };

  //only send line if you want to validate the viewModel there.
  const validateLineEditing = (editingLine?: SalesLineViewModel) => {
    if ((salesHeaderViewModel.propertiesChanged?.length ?? 0) > 0) {
      showSnackbar(t("dialog.saveBeforeLines"), "warning");
      return false;
    }

    if (editingLine && editingLine.itemNo && !editingLine.variantId && variants.length > 0) {
      showSnackbar(t("dialog.selectVariant"), "warning");
      return false;
    }

    if (editingLine && editingLine.isAccountCreate && (!editingLine.accountNo)) {
      showSnackbar(t("dialog.selectAccount"), "warning");
      return false;
    }

    if (editingLine && editingLine.isItemCreate && (!editingLine.itemNo || editingLine.itemNo.length === 0)) {
      showSnackbar(t("dialog.selectItem"), "warning");
      return false;
    }
    return true;

  }


  //chaos logic for fields

  const renderImage = () => {
    if (editingLine?.isNew) return null;

    return (
      <Grid item xs={12} sx={{ marginBottom: "1rem" }}>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Box sx={{ width: "100%", maxWidth: "120px", maxHeight: "120px", height: "auto", borderRadius: "8px" }}>
            <ImageBox
              imageUrl={editingLine?.img_Url ?? ""}
              altText={editingLine?.description || "No Description"}
              fallbackText={t("common.noImage")}
              width="120px"
              height="120px"
            />
          </Box>
        </Box>
      </Grid>
    );
  };

  const renderItemFields = () => {
    if (!editingLine) return null;
    if (
      editingLine.isCertificateCreate ||
      editingLine.isAccountCreate ||
      editingLine.accountNo ||
      editingLine.isTextCreate ||
      editingLine.isText
    ) return null;

    return (
      <>
        <Grid item xs={12} lg={6}>
          <FormControl fullWidth>
            <ItemAutoComplete
              label={t("common.itemNo")}
              valueId={editingLine?.itemNo}
              onChange={(selected) => {
                handleFieldChanges({
                  itemNo: selected?.value ?? null,
                  description: selected?.description ?? null,
                  variantId: null,
                });
              }}
              load
              disabled={!editingLine?.isNew}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} lg={6}>
          <FormControl fullWidth>
            <VariantAutoComplete
              itemNo={editingLine?.itemNo ?? ""}
              onChange={(selected) => handleFieldChange("variantId", selected?.id ?? null)}
              valueId={editingLine?.variantId}
              disabled={!editingLine?.itemNo || editingLine?.simpleItem === true || !editingLine?.isNew}
            />
          </FormControl>
        </Grid>
      </>
    );
  };

  const renderAccountFields = () => {
    if (!editingLine) return null;
    if (
      editingLine.isCertificateCreate ||
      editingLine.isItemCreate ||
      editingLine.itemNo ||
      editingLine.isTextCreate ||
      editingLine.isText
    ) return null;

    return (
      <Grid item xs={12}>
        <FormControl fullWidth>
          <AccountAutoComplete
            label={t("common.accountNo")}
            valueId={editingLine?.accountNo}
            onChange={(selected) => {
              handleFieldChanges({
                accountNo: selected?.value ?? null,
                description: selected?.description ?? null,
              });
            }}
            load
            disabled={locked === true}
          />
        </FormControl>
      </Grid>
    );
  };

  const renderCertificateField = () => {
    if (!editingLine) return null;
    if (!editingLine.isCertificateCreate) return null;

    return (
      <Grid item xs={12}>
        <FormControl fullWidth>
          <CertificateAccountAutoComplete
            label={t("common.certificateAccount")}
            valueId={editingLine?.accountNo}
            onChange={(selected) => handleFieldChange("accountNo", selected?.value ?? null)}
            load
            disabled={locked === true}
          />
        </FormControl>
      </Grid>
    );
  };

  const renderBarcodeField = () => {
    if (editingLine?.isNew || editingLine?.accountNo || editingLine?.isText) return null;

    return (
      <Grid item xs={12}>
        <TextField
          label={t("common.barcode")}
          variant="outlined"
          fullWidth
          value={editingLine?.barcode || ""}
          disabled
        />
      </Grid>
    );
  };

  const renderQuantityField = () => {
    if (!editingLine) return null;
    if (editingLine?.isTextCreate || editingLine?.isText) return null;

    return (
      <Grid item xs={12} lg={editingLine?.isNew !== true ? 4 : 12}>
        <LocalizedNumberInputs
          label={t("common.quantity")}
          value={editingLine.quantity ?? 1}
          onValueChange={(value) => handleFieldChange("quantity", value ?? null)}
          required
          error={editingLine.quantity == null}
          helperText={editingLine.quantity == null ? t("help.quantityRequired") : ""}
          allowNegative={false}
          decimalScale={4}
          thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
          decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
          disabled={locked === true}
        />
      </Grid>
    );
  };

  const renderQuantityToShipFields = () => {
    if (!editingLine) return null;
    if (editingLine?.isNew || editingLine?.isText) return null;

    return (
      <>
        <Grid item xs={12} lg={4}>
          <LocalizedNumberInputs
            label={t("common.qtyToShip")}
            value={editingLine.quantityToShip ?? null}
            onValueChange={(value) => handleFieldChange("quantityToShip", value ?? null)}
            required
            error={editingLine.quantityToShip == null}
            helperText={editingLine.quantityToShip == null ? t("help.qtyToShipRequired") : ""}
            allowNegative={false}
            decimalScale={4}
            thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
            decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
          />
        </Grid>

        <Grid item xs={12} lg={4}>
          <LocalizedNumberInputs
            label={t("common.quantity")}
            value={editingLine.outstandingQuantity ?? null}
            onValueChange={() => { }}
            allowNegative={false}
            decimalScale={4}
            thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
            decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
            disabled
          />
        </Grid>
      </>
    );
  };

  const renderPriceFields = () => {
    if (!editingLine) return null;
    if ((editingLine?.isNew && !editingLine?.accountNo && !editingLine.isAccountCreate) || editingLine?.isText) return null;

    const total =
      ((editingLine.unitPrice ?? 0) * (editingLine.quantity ?? 0)) -
      (editingLine.lineDiscountAmountIncVAT ?? 0);

    return (
      <>
        <Grid item xs={12} lg={6}>
          <LocalizedNumberInputs
            label={t("common.unitPrice")}
            value={editingLine.unitPrice ?? null}
            onValueChange={(value) => handleFieldChange("unitPrice", value ?? null)}
            required
            error={editingLine.unitPrice == null}
            helperText={editingLine.unitPrice == null ? t("help.unitPriceRequired") : ""}
            allowNegative={false}
            decimalScale={4}
            thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
            decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
            disabled={locked === true}
          />
        </Grid>

        <Grid item xs={12} lg={6}>
          <LocalizedNumberInputs
            label={t("common.lineDiscount")}
            value={editingLine.lineDiscountAmountIncVAT ?? null}
            onValueChange={(value) => handleFieldChange("lineDiscountAmountIncVAT", value ?? null)}
            required
            allowNegative={false}
            decimalScale={4}
            thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
            decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
            disabled={locked === true}
          />
        </Grid>

        <Grid item xs={12}>

          <LocalizedNumberInputs
            label={t("common.amount")}
            value={total}
            onValueChange={() => { }}
            required
            allowNegative={true}
            decimalScale={4}
            thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
            decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
            disabled={locked === true}
            color="success"
          />
        </Grid>
      </>
    );
  };


  const handleScannerOpen = useCallback(() => {
    setIsScannerOpen(true);
  }, []);

  const handleScannerClose = useCallback(() => {
    setIsScannerOpen(false);
  }, []);

  const scanBarcode = async (barcode: string) => {
    var barcodeLine = {
      id: 0,
      no: "",
      barcode: barcode,

      quantity: 1,
    
      quantityToShip: 1,
     
      documentNo: salesHeaderViewModel.no,
      isNew: true,
    }

    try {
      setIsLoading(true);
      setIsLineLoading(true);
      const updatedLine = await authService.post<SalesLineViewModel>("/orderApi/CreateOrUpdateOrderLineFromBarcode", barcodeLine);


      const exists = salesLines.some(line => line.id === updatedLine.id);

      if (exists) {
        // Update existing row
        setSalesLines(prev =>
          prev.map(line => (line.id === updatedLine.id ? updatedLine : line))
        );
      } else {
        // New row → navigate to the last page
        const newTotalCount = totalCount + 1;
        const lastPage = Math.ceil(newTotalCount / query.pageSize) - 1;

        setQuery(prev => ({
          ...prev,
          page: lastPage,
          totalCount: newTotalCount,
        }));
      }
      
      showSnackbar(t("dialog.lineSaved"), "success");
      setEditingLine(null);
      onLinesChanged?.();
    } catch (error: any) {
      console.error("Error saving line:", error);
      showSnackbar(`${t("dialog.lineSaveError")} : ${error.response?.data ?? error.message}`, "error");

    } finally {
      setIsLoading(false);
      setIsLineLoading(false);
    }
    return true;
  };

  const handleBarcodeScan = useCallback((code: string) => scanBarcode(code), [scanBarcode]);




  return (
    <Box sx={{ padding: "1rem" }}>
      <ScannerDialog
        open={isScannerOpen}
        onClose={handleScannerClose}
        onScan={handleBarcodeScan}
      />
      <Box
        sx={{
          backgroundColor: "white",
          color: "white",
          borderRadius: "4px",
          marginBottom: "1rem",
          display: "flex",
          justifyContent: "center", // Center buttons horizontally
        }}
      >
        {!history && locked !== true && (
          <Grid container spacing={2} justifyContent="center" alignItems="center">
            {/* Add New Line */}
            <Grid item xs={12} sx={{ textAlign: "center" }}>
              <Button variant="contained" onClick={async () => handleAddNewLine("item")}
                startIcon={<AddIcon />}
                size="small"
              >
                {t("actions.addItem")}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showAddAdvanced}
                    onChange={(e) => setShowAddAdvanced(e.target.checked)}
                  />
                }
                label={t("common.showAdvancedControls")}
                sx={{ color: "black" }}
              />
            </Grid>
            {showAddAdvanced && (
              <>
                <Grid item xs={12} sx={{ textAlign: "center" }}>
                  <Button variant="contained" onClick={async () => handleScannerOpen()}
                    startIcon={<DocumentScannerIcon />}
                    size="small"
                  >
                    {t("common.scanBarcode")}
                  </Button>
                </Grid>
                <Grid item xs={12} sx={{ textAlign: "center" }}>
                  <Button variant="contained" onClick={async () => handleAddNewLine("account")}
                    startIcon={<AddIcon />}
                    size="small"
                  >
                    {t("actions.addAccount")}
                  </Button>
                </Grid>

                <Grid item xs={12} sx={{ textAlign: "center" }}>
                  <Button variant="contained" onClick={async () => handleAddNewLine("certificate")}
                    startIcon={<AddIcon />}
                    size="small"
                  >
                    {t("actions.addCertificate")}
                  </Button>
                </Grid>
                <Grid item xs={12} sx={{ textAlign: "center" }}>
                  <Button variant="contained" onClick={async () => handleAddNewLine("text")}
                    startIcon={<AddIcon />}
                    size="small"
                  >
                    {t("actions.addText")}
                  </Button>
                </Grid>
                <Grid item xs={12} sx={{ textAlign: "center" }}>
                  <Button
                    variant="contained"
                    startIcon={<DeleteIcon />}
                    color="error"
                    size="small"
                    onClick={async () => deleteAll()}
                  >
                    {t("actions.deleteAll")}
                  </Button>
                </Grid>
              </>
            )}




          </Grid>
        )}
      </Box>

      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={() => toggleRowMode()}
      >
        {t("common.toggleListMode")}
      </Button>

      <Box sx={{ padding: "1rem" }}>
        {rowMode ? (
          <Grid container spacing={1}>
            {!isLinesLoading &&
              salesLines.map((line, index) => (
                <Grid item xs={12} key={line.id}>
                  <Grid
                    container
                    spacing={1}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "transparent" : "#fafafa",
                      ":hover": {
                        backgroundColor: index % 2 === 0 ? "#f0f0f0" : "#e8e8e8",
                      },
                    }}
                  >
                    <Grid item xs={12} lg={3} sx={{ display: "flex", justifyContent: "center" }}>
                      <Box
                        sx={{
                          width: "100%",
                          maxWidth: "80px",
                          maxHeight: "80px",
                          height: "auto",
                          borderRadius: "8px",
                        }}
                      >
                        <ImageBox
                          imageUrl={line.img_Url ?? ""}
                          altText={line.description || "No Description"}
                          fallbackText={t("common.noImage")}
                          width="80px"
                          height="80px"
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} lg={4}>
                      <Typography variant="body2" sx={{ color: "gray" }}>
                        {line.itemNo ?? line.accountNoDesc}
                      </Typography>
                      <Typography variant="body1">{line.description}</Typography>
                      <Typography variant="caption">{line.barcode}</Typography>
                      <Typography variant="body2">{generateVariantLabel(line)}</Typography>

                    </Grid>

                    <Grid item xs={12} lg={3}>
                      <Typography variant="body2" sx={{ color: "gray" }}>
                        {t("common.qty")}: {formatNumber(line.quantity)}
                      </Typography>
                      <Typography variant="body1">
                        {t("common.amount")}: {formatNumber(line.amountIncludingVat)} {line.currencyCode}
                      </Typography>
                    </Grid>
                    {!history && (
                      <>
                        <Grid item xs={12} lg={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
                          <Tooltip title={t("common.edit")}>
                            <IconButton color="secondary" onClick={() => handleEditRow(line)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                        {locked !== true && (
                          <Grid item xs={12} lg={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Tooltip title={t("common.delete")}>
                              <IconButton
                                color="error"
                                onClick={async () => {
                                  await handleRowDelete(line);
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Grid>
                        )}
                      </>
                    )}
                  </Grid>
                </Grid>
              ))}
          </Grid>

        ) : (
          <Grid container spacing={2} sx={{ mb: 2 }} justifyContent="center" alignItems="center">
            {!isLinesLoading && salesLines.map((line) => (
              <Grid item

                sm={salesLines.length === 1 ? 12 : 6}
                md={salesLines.length === 1 ? 12 : 6}
                key={line.id}>
                <Card variant="outlined">
                  {!history && (
                    <Toolbar sx={{ backgroundColor: "primary.light", color: "white" }}>
                      <Grid container spacing={1}>
                        <Grid item xs={4}>
                          <Typography variant="h6" fontWeight="bold">
                            {line.lineNo}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Tooltip title={t("common.edit")}>
                            <IconButton color="secondary" onClick={() => handleEditRow(line)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                        {locked !== true && (
                          <Grid item xs={4}>
                            <Tooltip title={t("common.delete")}>
                              <IconButton color="error" onClick={async () => {
                                await handleRowDelete(line);
                              }}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Grid>
                        )}
                      </Grid>
                    </Toolbar>


                  )}
                  <CardContent>
                    <Grid container spacing={1}>
                      {/* Image */}


                      <Grid item xs={12} sx={{ marginBottom: "1rem" }}>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <Box sx={{ width: "100%", maxWidth: "120px", maxHeight: "120px", height: "auto", borderRadius: "8px" }}>
                            <ImageBox
                              imageUrl={line.img_Url ?? ""}
                              altText={line.description || "No Description"}
                              fallbackText={t("common.noImage")}
                              width="120px"
                              height="120px"

                            />
                          </Box>
                        </Box>
                      </Grid>



                      {/* Item No - Bold & Larger */}
                      <Grid item xs={12}>
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>{line.itemNo ?? line.accountNoDesc}</Typography>
                      </Grid>

                      {/* Description */}
                      <Grid item xs={12}>
                        <Typography sx={{ color: "gray" }}>{line.description}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ color: "gray" }}> {generateVariantLabel(line)}</Typography>
                      </Grid>

                      {/* Barcode & Quantity */}
                      {line.barcode && (
                        <Grid item md={6} xs={12}>
                          <Typography variant="body2" sx={{ color: "gray" }}>{t("common.barcode")}:</Typography>
                          <Typography variant="body1">{line.barcode}</Typography>
                        </Grid>
                      )}
                      <Grid item md={line.barcode ? 6 : 12} xs={12}>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>{t("common.qty")}:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>{formatNumber(line.quantity)}</Typography>
                      </Grid>

                      {/* Unit Price & Line Discount */}
                      <Grid item md={6} xs={12}>
                        <Typography variant="body2" sx={{ color: "gray" }}>{t("common.unitPrice")}:</Typography>
                        <Typography variant="body1">{formatNumber(line.unitPrice)}</Typography>
                      </Grid>
                      <Grid item md={6} xs={12}>
                        <Typography variant="body2" sx={{ color: "gray" }}>{t("common.discount")}:</Typography>
                        <Typography variant="body1">{formatNumber(line.lineDiscountAmountIncVAT)}</Typography>
                      </Grid>

                      {/* Amount - Highlighted */}
                      <Grid item xs={12}>
                        <Box sx={{ backgroundColor: "lightgreen", padding: "0.5rem", borderRadius: "4px", textAlign: "center" }}>
                          <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                            {t("common.amount")}: {formatNumber(line.amountIncludingVat)} {line.currencyCode}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>

                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
      <Dialog
        open={!!editingLine}
        onClose={() => setEditingLine(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "8px", maxHeight: "80vh", overflowY: "auto" } }}
      >
        <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="h6">{editingLine?.isNew ? t("common.create") : t("common.editLine")}</Typography>

          {editingLine && (
            <>
              {renderImage()}
              <Grid container spacing={2}>
                {renderItemFields()}
                {renderAccountFields()}
                {renderCertificateField()}
                <Grid item xs={12}>
                  <TextField
                    label={t("common.description")}
                    variant="outlined"
                    fullWidth
                    value={editingLine?.description || ""}
                    onChange={(e) => handleFieldChange("description", e.target.value)}
                  />
                </Grid>
                {renderBarcodeField()}
                {renderQuantityField()}
                {renderQuantityToShipFields()}
                {renderPriceFields()}
              </Grid>
            </>
          )}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <Button onClick={handleSaveLine} variant="contained" startIcon={<SaveIcon />} disabled={!editingLine || history}>
              {t("common.save")}
            </Button>
            <Button onClick={() => setEditingLine(null)} variant="outlined" startIcon={<CancelIcon />}>
              {t("common.cancel")}
            </Button>

          </Box>
        </Box>
      </Dialog>

      <ResponsivePagination
        page={query.page}
        pageSize={query.pageSize}
        totalCount={totalCount}
        onPageChange={(newPage) => setQuery({ ...query, page: newPage })}
        sx={{ marginTop: "1rem" }}
      />
      {/*<Grid container spacing={2} justifyContent="center" alignItems="center" sx={{marginTop:"1rem"}}>*/}
      {/*    */}{/* Add New Line */}
      {/*    <Grid item xs={12} sx={{ textAlign: "center" }}>*/}
      {/*        <Tooltip title="Export to Excel">*/}
      {/*            <Button variant="contained" startIcon={<TableChartIcon />} color="success">*/}
      {/*                Excel*/}
      {/*            </Button>*/}

      {/*        </Tooltip>*/}
      {/*    </Grid>*/}

      {/*    */}{/* Export to PDF */}
      {/*    <Grid item xs={12} sx={{ textAlign: "center" }} >*/}
      {/*        <Tooltip title="Export to PDF">*/}
      {/*            <Button variant="contained" startIcon={<PictureAsPdfIcon />} color="secondary">*/}
      {/*                PDF*/}
      {/*            </Button>*/}

      {/*        </Tooltip>*/}
      {/*    </Grid>*/}
      {/*</Grid>*/}
    </Box>
  );
};

export default SalesDocumentLines;